// components/NotificationBell.jsx — Anamoria SPA
// v1.2 — NEW-3: type-aware landing tab. The bell already renders any notification message
//   generically; this adds routing so a leader-only outcome (share_decline / memory_unshared)
//   lands on the "To review" tab (where those pieces live), while a share_agree lands on Shared
//   (the piece is now group-visible). Other types unchanged. Dark behind group_spaces_enabled.
// v1.1 — Fix same-route notification navigation (May 27, 2026)
//
// Changes from v1.0:
//   - handleNotificationClick: added _navKey (Date.now()) to router state.
//     React Router treats same-path navigations as no-ops unless state
//     changes. The unique _navKey forces a new location object, which
//     triggers useLocation() re-renders in SpacePage so the tab switch
//     and memory highlight actually fire.
//   - handleNotificationClick: now passes memoryId in state so SpacePage
//     can forward it to MemoryFeed for scroll-to-memory highlighting.
//   - Zero other changes. Bell icon, badge, dropdown, mark-read, outside
//     click, fetch — all byte-identical to v1.0.
//
// Props:
//   getApi — function returning api client (from SpacePage)

import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './NotificationBell.module.css';

function BellIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  );
}

function formatTimeAgo(dateStr) {
  try {
    const now = Date.now();
    const then = new Date(dateStr).getTime();
    const diffMin = Math.floor((now - then) / 60000);
    if (diffMin < 1) return 'Just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    const diffDay = Math.floor(diffHr / 24);
    if (diffDay === 1) return 'Yesterday';
    if (diffDay < 7) return `${diffDay}d ago`;
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch { return ''; }
}

export default function NotificationBell({ getApi }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const dropdownRef = useRef(null);
  const bellRef = useRef(null);

  // Fetch notifications on mount
  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const api = getApi();
        const data = await api.get('/notifications');
        if (!cancelled) {
          setItems(data.items || []);
          setUnreadCount(data.unreadCount || 0);
          setLoaded(true);
        }
      } catch (err) {
        console.error('Notification fetch error:', err);
        if (!cancelled) setLoaded(true);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [getApi]);

  // Close dropdown on outside click
  useEffect(() => {
    if (!open) return;
    function handleClickOutside(e) {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target) &&
        bellRef.current && !bellRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  // Mark unread items as read when dropdown opens
  const handleOpen = useCallback(async () => {
    const willOpen = !open;
    setOpen(willOpen);
    if (willOpen && unreadCount > 0) {
      const unreadIds = items.filter(i => !i.readAt).map(i => i.id);
      if (unreadIds.length === 0) return;
      try {
        const api = getApi();
        const result = await api.post('/notifications/read', { ids: unreadIds });
        setUnreadCount(result.unreadCount ?? 0);
        setItems(prev => prev.map(item =>
          unreadIds.includes(item.id) ? { ...item, readAt: new Date().toISOString() } : item
        ));
      } catch (err) {
        console.error('Mark read error:', err);
      }
    }
  }, [open, unreadCount, items, getApi]);

  // v1.1: Navigate to the space's Shared tab (contributor memories are always shared)
  // _navKey ensures React Router treats same-path navigation as a new location,
  // which triggers useLocation() re-renders in SpacePage even when already on that space.
  // memoryId allows MemoryFeed to scroll to and highlight the specific memory.
  function handleNotificationClick(notification) {
    setOpen(false);
    // NEW-3: a leader-only outcome lands on "To review" (where the piece is); share_agree and all
    // legacy types land on Shared. (share_request targets a contributor, whose surface is the feed
    // card prompt, not this owner bell.)
    const leaderOnlyOutcome = notification.type === 'share_decline' || notification.type === 'memory_unshared';
    navigate(`/spaces/${notification.spaceId}`, {
      state: {
        tab: leaderOnlyOutcome ? 'pending' : 'shared',
        memoryId: notification.memoryId || null,
        _navKey: Date.now(),
      },
    });
  }

  if (!loaded) return null;

  return (
    <div className={styles.bellContainer}>
      <button
        ref={bellRef}
        className={styles.bellBtn}
        onClick={handleOpen}
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
      >
        <BellIcon />
        {unreadCount > 0 && <span className={styles.badge} />}
      </button>

      {open && (
        <div ref={dropdownRef} className={styles.dropdown}>
          <div className={styles.dropdownHeader}>
            <span className={styles.dropdownTitle}>Notifications</span>
          </div>

          {items.length === 0 ? (
            <div className={styles.empty}>
              <p className={styles.emptyText}>No notifications yet</p>
            </div>
          ) : (
            <div className={styles.list}>
              {items.map(item => (
                <button
                  key={item.id}
                  className={`${styles.row} ${!item.readAt ? styles.rowUnread : ''}`}
                  onClick={() => handleNotificationClick(item)}
                >
                  <span className={styles.rowInitial}>
                    {(item.actorName || '?').charAt(0).toUpperCase()}
                  </span>
                  <div className={styles.rowContent}>
                    <span className={styles.rowMessage}>{item.message}</span>
                    <span className={styles.rowTime}>{formatTimeAgo(item.createdAt)}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
